import { Copy, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Badge } from '../atoms/Badge'
import { Icon } from '../atoms/Icon'
import { StatusDot, type StatusDotProps } from '../atoms/StatusDot'
import { Card } from '../card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../dropdown-menu'
import { formatRelative } from './FreshnessBanner'

/**
 * CustomFlowCard — one card in the Custom Flows list.
 *
 * Renders flow name, slug (as a monospace caption), an outlined status pill
 * (matches the InvocationTraceRow pattern — outlined Badge + StatusDot for
 * a contrast-safe semantic), version label, and a "Last modified N min ago"
 * caption. The right-hand corner hosts an icon button that opens a DS
 * DropdownMenu with Open / Duplicate / Delete actions.
 *
 * Composes: Card (DS), DropdownMenu (DS), Badge (atom), Icon (atom),
 * StatusDot (atom), formatRelative from FreshnessBanner.
 *
 * Card uses role="article" with an accessible name from the flow name so
 * screen-reader users hear "Article, Sign-in pipeline, draft, version 3".
 * The whole card is clickable (calls `onOpen` when present) but the
 * action-menu button stops propagation so opening the menu doesn't also
 * open the flow.
 */

export type CustomFlowStatus = 'draft' | 'published' | 'archived'

export interface CustomFlowSummary {
  id: string
  name: string
  slug: string
  status: CustomFlowStatus
  version: number
  lastModifiedAt: string
}

export interface CustomFlowCardProps {
  flow: CustomFlowSummary
  /** Called when the card body is clicked. Menu actions never fire this. */
  onOpen?: (id: string) => void
  /** Called from the "Duplicate" menu item. */
  onDuplicate?: (id: string) => void
  /** Called from the "Delete" menu item (destructive). */
  onDelete?: (id: string) => void
  /**
   * Override the "now" used for relative-time computation. Stories + tests
   * set this so output stays deterministic.
   */
  nowFn?: () => Date
  /** Extra classes for the outer Card. */
  className?: string
}

interface StatusSpec {
  dot: NonNullable<StatusDotProps['status']>
  label: string
}

const STATUS_SPEC: Record<CustomFlowStatus, StatusSpec> = {
  draft: { dot: 'pending', label: 'Draft' },
  published: { dot: 'active', label: 'Published' },
  archived: { dot: 'inactive', label: 'Archived' },
}

export const CustomFlowCard = React.forwardRef<HTMLDivElement, CustomFlowCardProps>(
  ({ flow, onOpen, onDuplicate, onDelete, nowFn, className }, ref) => {
    const spec = STATUS_SPEC[flow.status]
    const relative = React.useMemo(
      () => formatRelative(flow.lastModifiedAt, nowFn ? nowFn() : new Date()),
      [flow.lastModifiedAt, nowFn],
    )

    // Containment guard: only treat events that landed on the card root
    // (not on the action-menu trigger, not on a menu item) as "open the
    // flow" intents. Using stopPropagation on the trigger is fragile when
    // Radix composes handlers through asChild + Slot — explicit containment
    // here is robust.
    const cardRootRef = React.useRef<HTMLDivElement | null>(null)
    const menuShellRef = React.useRef<HTMLDivElement | null>(null)
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        cardRootRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      },
      [ref],
    )

    /**
     * A click is "from outside the openable surface" when either:
     *   - the DOM target is the action-menu trigger or any of its children
     *     (covered by `menuShellRef.contains(target)`); OR
     *   - the DOM target is not inside the card DOM at all (Radix portals
     *     the DropdownMenuContent to document.body, but React still bubbles
     *     events through the virtual tree up to the Card — that bubble must
     *     not trigger `onOpen`).
     */
    const isFromMenuRegion = React.useCallback((target: EventTarget | null) => {
      const node = target as Node | null
      if (!node) return false
      if (menuShellRef.current?.contains(node)) return true
      if (!cardRootRef.current?.contains(node)) return true
      return false
    }, [])

    const handleCardClick = React.useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isFromMenuRegion(e.target)) return
        onOpen?.(flow.id)
      },
      [onOpen, flow.id, isFromMenuRegion],
    )

    const handleCardKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!onOpen) return
        if (isFromMenuRegion(e.target)) return
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(flow.id)
        }
      },
      [onOpen, flow.id, isFromMenuRegion],
    )

    const isInteractive = !!onOpen

    return (
      <Card
        ref={setRefs}
        role="article"
        aria-label={`${flow.name}, ${spec.label}, version ${flow.version}`}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={isInteractive ? handleCardClick : undefined}
        onKeyDown={isInteractive ? handleCardKeyDown : undefined}
        className={cn(
          'p-4 flex flex-col gap-3',
          isInteractive && [
            'cursor-pointer transition-[border-color,box-shadow] duration-150',
            'border border-transparent hover:border-ia-border-strong/60',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          ],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <h3 className="truncate text-body-md font-semibold text-ia-text">{flow.name}</h3>
            <code className="truncate text-caption text-ia-text-secondary font-mono">
              {flow.slug}
            </code>
          </div>

          <div ref={menuShellRef}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Actions for ${flow.name}`}
                  // The menu trigger lives inside the clickable card — stop
                  // propagation so opening the menu doesn't also open the
                  // flow.
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-pill shrink-0',
                    'text-ia-text-secondary transition-[background-color,color] duration-150',
                    'hover:bg-ia-elevated hover:text-ia-text',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  )}
                >
                  <Icon icon={MoreHorizontal} size="sm" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {onOpen && (
                  <DropdownMenuItem
                    onSelect={() => onOpen(flow.id)}
                    aria-label={`Open ${flow.name}`}
                  >
                    <Icon icon={Pencil} size="xs" />
                    Open
                  </DropdownMenuItem>
                )}
                {onDuplicate && (
                  <DropdownMenuItem
                    onSelect={() => onDuplicate(flow.id)}
                    aria-label={`Duplicate ${flow.name}`}
                  >
                    <Icon icon={Copy} size="xs" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => onDelete(flow.id)}
                      aria-label={`Delete ${flow.name}`}
                      className="text-ia-error focus:bg-ia-error-bg focus:text-ia-error"
                    >
                      <Icon icon={Trash2} size="xs" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 min-w-0">
          <Badge variant="outlined" size="sm" aria-label={`Status: ${spec.label}`}>
            <StatusDot status={spec.dot} size="sm" aria-hidden="true" />
            {spec.label}
          </Badge>
          <div className="flex items-center gap-3 text-caption text-ia-text-secondary shrink-0">
            <span className="font-mono tabular-nums" title={`Version ${flow.version}`}>
              v{flow.version}
            </span>
            <span aria-hidden="true">·</span>
            <span title={flow.lastModifiedAt}>{relative === '—' ? '—' : `${relative} ago`}</span>
          </div>
        </div>
      </Card>
    )
  },
)
CustomFlowCard.displayName = 'CustomFlowCard'
